import _ from "lodash";
import { useContext, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import type { AccountWORiskAllocation } from "../../../../api/src/accounts/accounts.service";
import type { AccountCashSetting } from "../../../../api/src/cash-settings/lib";
import Loading from "../../Loading";
import { useQueryFeeStructures } from "../../billing/lib";
import { useQueryAccountCashSettings } from "../../cash/lib";
import { displayAccountName } from "../../lib/display";
import HouseholdCashBillingTable, {
  HouseholdCashBillingRow,
} from "./HouseholdCashBillingTable";
import { HouseholdContext } from "./HouseholdInfo";
import { HouseholdWithAccounts } from "../../../../api/src/households/households.service";

const HouseholdCashBillingRebalance = ({
  household: propHousehold,
}: {
  household?: HouseholdWithAccounts;
}) => {
  const { household: contextHousehold } = useContext(HouseholdContext);
  const household = propHousehold ?? contextHousehold;

  const { data: dataFeeStructures } = useQueryFeeStructures();
  const feeStructures = dataFeeStructures?.data;

  // Every page of `GET /v1/cash-settings/accounts` (APP-1651).
  const { isPending: isPendingCashSettings, data: dataCashSettings } =
    useQueryAccountCashSettings();
  const cashSettings = dataCashSettings?.data;

  const combinedCashSettingsAccountsHouseholds = useMemo(() => {
    const cashSettingsMap: Record<number, AccountCashSetting> = _.keyBy(
      cashSettings ?? [],
      "accountId",
    );
    const accountsMap: Record<number, AccountWORiskAllocation> = _.keyBy(
      household?.accounts ?? [],
      "id",
    );
    const feeStructuresMap = _.keyBy(feeStructures ?? [], "feeStructureId");

    return Object.entries(accountsMap).map(([accountId, account]) => {
      const cashSetting = cashSettingsMap[parseInt(accountId)];

      const feeStructureId =
        account.feeStructureId ?? household?.feeStructureId ?? null;

      // Catch cases where a relocated account is no longer active
      const feeLocationAccountId =
        account.feeLocationAccountId == null ||
        typeof accountsMap[account.feeLocationAccountId] === "undefined"
          ? null
          : account.feeLocationAccountId;

      const result: HouseholdCashBillingRow = {
        ...account,
        ...(typeof cashSetting === "undefined" ? {} : cashSetting),
        cashSettingId: account.cashSettingId,
        feeStructureId: feeStructureId,
        feeStructureName:
          feeStructureId == null
            ? undefined
            : feeStructuresMap[feeStructureId]?.name,
        isFeeStructureInherited:
          feeStructureId != null && account.feeStructureId == null,
        feeLocation: account.feeLocation,
        feeLocationAccountId,
        feeLocationAccountName:
          account.feeLocation !== "A" || feeLocationAccountId == null
            ? undefined
            : displayAccountName(
                accountsMap[feeLocationAccountId].displayName,
                accountsMap[feeLocationAccountId].displayNumber,
              ),
      };

      return result;
    });
  }, [cashSettings, feeStructures, household]);

  return (
    <Row className="mb-3">
      <Col>
        <h3>Account Cash Settings</h3>
        {isPendingCashSettings ? (
          <Loading message="Accounts" />
        ) : (
          <HouseholdCashBillingTable
            accounts={combinedCashSettingsAccountsHouseholds}
          />
        )}
      </Col>
    </Row>
  );
};

export default HouseholdCashBillingRebalance;
